<template>
  <div class="room-chat-form-component" 
       :class="[
           type, 
           `section-${section}`, 
           {'mine': isMine}, 
           {'fix': isFixed},
           {'whisper': isWhisper},
           {'staff': isWhisperStaff},
           ]"
       @click="switchChatTypeToWhisper"
  >
    <div class="room-chat-form">
      <GrmAvatar v-if="!isMyChat&&!isSystemNoti"
                 :icon="profileIcon"
                 :src="avatarSrc"
      />
      <div class="room-chat">
        <div class="room-chat-info"
             v-if="!isMyChat&&!isSystemNoti">
          <GrmIcon icon="opener" class="!w-4 mr-1" v-if="isOpener(item.userId)&&!item.isFile" />
          <p class="room-chat-user-nickname sender" v-tooltip="item.nickname" v-html="item.nickname"></p>
          <p class="room-chat-user-nickname receiver" v-tooltip="item.receiveUser" v-html="item.receiveUser"></p>
          <p class="room-chat-time"
             v-if="item.receiveDate">{{ item.receiveDate }}</p>
        </div>
        
        <!-- my-chat-not-yet-responded -->
        <template v-if="isMyChat">
          <div class="room-chat-message-wrapper" style="opacity: 0.6;">
            <div class="room-chat-message-loading">
              <p class="room-chat-message-loading-text">{{ $t("chat.form.sending") }}</p>
            </div>
            <pre class="room-chat-message me" v-html="item.myChats" />
          </div>
        </template>

        <!-- file, record -->
        <template v-if="item.isFile">
          <div class="room-chat-file-info">
            <p class="room-chat-file-name"
               :class="{'line-through': item.del}"
               v-if="item.title">{{item.title}}</p>
            <div class="room-chat-file-detail-info">
              <p class="room-chat-file-size">{{item.filesize}}</p>
              <p class="room-chat-file-time" 
                 v-if="item.duration">{{item.duration}}</p>
            </div>
            <p class="room-chat-file-period"
                    v-if="item.expireDate">{{ $t("chat.form.expireDate") }} : ~ {{item.expireDate}}</p>

            <!-- progress -->
            <div v-if="isDownloading || isUploding" class="room-chat-file-progress">
              <div class="room-chat-file-percent">
                <p class="room-chat-file-percent-number">{{ item.progress }}</p>%
              </div>
              <div class="room-chat-file-progress-bar-wrapper">
                <div class="room-chat-file-progress-bar" 
                     :style="progressBarStyle"></div>
              </div>
            </div>

            <!-- download -->
            <button v-else-if="!hideDownload || isPreparing" class="room-chat-file-download-button"
                    :class="{'preparing': isPreparing}"
                    @click="$emit('onClickDownload')"></button>
          </div>
        </template>

        <!-- default -->
        <template v-else> <!--notice, chat-->
          <div class="room-chat-message-wrapper">
            <div class="relative">
              <pre class="room-chat-message" :class="[item.type === 'stt' && 'stt']" v-html="content"
                @click="$emit('onChangeNoticeSection')"/>
            </div>
            <div v-if="isSystemNoti" 
                class="room-chat-noti-time">{{ item.date }}</div>
            <div class="relative">
              <pre class="room-chat-message translation" v-if="item.type === 'stt' && item.translatedText">{{item.translatedText}}</pre>
            </div>
          </div>
        </template>

        <div class="room-chat-more"
             v-if="hasEmceeRole() && !isMyChat && !isWhisper && chatMoreList.length">
          <button class="room-chat-more-button" 
                  @click="onToggleOpenChatMoreList"></button>
          <GrmMenu text-align="left"
                     :list="chatMoreList"
                     :open.sync="isOpenChatMoreList"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash"; 
import {mapActions, mapGetters, mapMutations} from "vuex";
import GrmMenu from "@/components/Common/menu/GrmMenu";
import GrmAvatar from "@/components/Common/avatar/GrmAvatar";
import GrmIcon from "@/components/Common/icon/GrmIcon";

export default {
  name: 'RoomChatForm',
  components: {
    GrmIcon,
    GrmAvatar,
    GrmMenu,
  },
  props: {
    section: {
      type: String,
      default: 'chat',
      // required: true
    },
    type: {
      type: String,
      // required: true
    },
    item: {
      type: Object,
      required: true
    },
    menuList: {
      type: Array,
      default: () => [],
    },
    hideDownload: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isOpenChatMoreList: false,
    }
  },
  computed: {
    ...mapGetters('user', [
      'me',
      'isOpener',
      'getRoomUser',
      'hasEmceeRole',
      'hasSpeakerRole',
      'isParticipantRole',
    ]),
    isSystemNoti(){
      return this.type == 'systemMsg'
    },
    isChat(){
      return this.type == 'chat'
    },
    isNotice(){
      return this.type == 'notice'
    },
    isRecord(){
      return this.type == 'record'
    },
    isRecordSection(){
      return this.section == 'record'
    },
    isChatSection(){
      return this.section == 'chat'
    },
    isNoticeSection(){
      return this.section == 'notice'
    },
    isMyChat(){
      return this.type == 'my-chat'
    },
    isFixed(){
      return this.item?.fixed
    },
    isDownloading(){
      return this.item.downloadState == 'downloading'
    },
    isPreparing(){
      return this.item.downloadState == 'preparing'
    },
    isUploding(){
      return this.item.downloadState == 'uploading'
    },
    isWhisper(){
      return this.item.target == 'whisper'
    },
    isWhisperStaff(){
      return this.item.target === 'staff'
    },
    profileIcon(){
      if(this.isNoticeSection)
        return '';
      else if(this.isWhisper || this.isWhisperStaff)
        return this.item.target;
      else
        return this.type;
    },
    chatMoreList() {
      let list = [];
      
      if (this.isChat) {
        list = [{
          title: this.$t('chat.form.notice.post'),
          icon: 'notice',
          click: () => {
            if(!this.hasEmceeRole())
              return;

            const noticeData = {
              msg: this.item.text,
              fixed: true,
            }

            this.createNotice(noticeData);
          }
        }]
      } 
      
      if(!this.isChatSection){
        list = _.concat(
            list, {
            title: this.$t('common.delete'),
            icon: 'trash_red',
            click: () => {
              this.$emit('onClickDelete', this.item)
            }
          },
        )
      }
      return _.concat(this.menuList, list);
    },
    progressBarStyle() {
      return {
        width: `${this.item.progress}%`
      }
    },
    getProgressRate() {
      return recordFile => {
        if(!recordFile.downloadState)
          return '';
        else if(recordFile.downloadState != 'downloading')
          return `-(${recordFile.downloadState})`;
        else
          return `-(${recordFile.progress}%)`;
      }
    },
    content() {
      if(!this.item) return
      let content = this.item.text ||
          this.item.content;
      return content && content.escapeXml();
    },
    isMine(){
      return this.isMyChat || this.item?.userId == this.me.userId;
    },
    userRoleName() {
      return userId => {
        const roleId = this.getRoomUser(userId)?.roomUserRole.roleId;
        if (!roleId) {
          return '';
        }

        switch (roleId) {
          case 'speaker':
            return this.$t('common.speaker');
          case 'emcee':
            return this.$t('common.emcee');
          case 'participant':
            return '';
          default:
            return 'UNKNOWN';
        }
      }
    },
    avatarSrc () {
      return this.item?.userId ? process.env.VUE_APP_S3_IMAGE_URL + '/profile_img/users/' + this.item.userId : '';
    }
  },
  methods: {
    ...mapActions('notice', [
      'createNotice',
    ]),
    ...mapMutations('chat', [
      'SET_WHISPER_TYPE',
      'SET_WHISPER_TARGET'
    ]),
    onToggleOpenChatMoreList() {
      this.isOpenChatMoreList = !this.isOpenChatMoreList
    },
    switchChatTypeToWhisper() {
      if (this.item.target !== 'whisper') {
        return;
      }
      const targetUserId = (this.me.userId !== this.item.userId)
        ? this.item.userId
        : this.item.toUserId;

      this.SET_WHISPER_TYPE('whisper');
      this.SET_WHISPER_TARGET(targetUserId);
    }
  }
}
</script>

<style lang="scss" scoped>
.room-chat-form-component {
  &.mine.chat{
    .room-chat-user-nickname {
      &.sender {
        color: $mainColor;
      }
    }
  }
  &:not(.mine){
    .room-chat-user-nickname {
      &.receiver {
        color: $mainColor;
      }
    }
  }
  
  &.whisper, &.staff {
    background-color: #f8f8f8;

    .room-chat-info{
      padding-right: 0;
    }
    .room-chat-form {
      padding: 16px;
    }
    .room-chat-user-nickname {
      &.receiver {
        display: inline-block;
        padding-left: 18px;
        position: relative;
        vertical-align: middle;
        &::before {
          content: '';
          display: block;
          width: 6px;
          height: 6px;
          position: absolute;
          top: 5px;
          left: 6px;
          background: url('~@/assets/images/arrow_down_triangular_filled.svg') no-repeat center / 100% auto;
          transform: rotate(-90deg);
        }
      }
    }
  }
  &.notice {
    .room-chat-user-nickname {
      color: #333;
    }
    .room-chat-file-download-button {
      background-color: #fff;
      &.preparing {
        cursor: progress;
        pointer-events: none;
        &:hover {
          background-color: darkgrey;
        }
      }
      &:hover {
        background-color: $mainColor;
      }
    }
    .room-chat-more-button {
      &:hover {
        background-color: #fff;
      }
    }
  }
  &.record {
    .room-chat-time{
      margin-left: 4px;
      font-size: 11px;
      font-weight: 400;
      color: #59f;
    }
  }
  &.section-chat.notice {
    .room-chat-form {
      border-bottom: 1px solid rgba(0, 0, 0, 0.04);
      padding: 16px 8px 16px 16px;
      background-color: #f0f5fc;
    }
    .room-chat-message{
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      //margin-right: 20px;
      cursor: pointer;
    }
  }
  &.section-notice {
    &.fix {
      .room-chat-form {
        background-color: #f8f8f8;
      }
    }
    .room-chat-message{
      cursor: default;
    }
  }
  &.systemMsg {
    margin-top: 1px;
    
    .room-chat-form {
      padding: 8px 16px;
      background-color: #f8f8f8;
      
      .room-chat-message-wrapper {
        display: flex;
        
        .room-chat-message {
          width: calc(100% - 40px);
          font-size: 12px;
          color: #999;
          word-break: break-all;
          word-wrap: break-word;
          white-space: pre-wrap;
          line-height: 1.6;
        }
        
        .room-chat-noti-time {
          margin-top: 4px;
          font-size: 11px;
          color: #bbb;
        }
      }
    }
  }
  &.stt {
    .room-chat-message {
      border-radius: 8px;
      padding: 10px;
      background: rgba(2,168,226,0.15);
    }
  }
}
.room-chat-form {
  padding: 16px 8px 8px 16px;
  display: flex;
  gap: 0 12px;
}
.room-chat {
  min-width: 0;
  flex: 1;
  position: relative;
}
.room-chat-info {
  flex: 1;
  min-width: 0;
  margin-bottom: 6px;
  padding-right: 40px;
  display: flex;
  align-items: center;
  cursor: default;
}
.room-chat-user-nickname {
  flex: 1;
  min-width: 0;
  max-width: max-content;
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 16px;
}
.my-chat {
  .room-chat-form {
    padding: 0px 8px 8px 16px;
  }
  .room-chat-message {
    //text-align: right;
    padding-left: 40px;
  }
  .room-chat-message-wrapper {
    display: flex;
    align-items: center;
    position: relative;
    //transform: translateY(-8px);
  }
}
.room-chat-time {
  margin-left: 10px;
  font-size: 10px;
  color: #999;
  white-space: nowrap;
}
.room-chat-message-wrapper {
  position: relative;
}
.room-chat-message-wrapper{
  .stt::before{
    content: 'stt';
    position: absolute;
    font-size: 11px;
    top: -6px;
    left: -10px;
    padding: 1px 4px;
    background: #f8f8f8;
    box-shadow: 1px 1px 1px 0 rgba(0,0,0,0.4);
    border-radius: 5px;
  }
  .translation{
    color: $mainColor;
  }
  .translation::before{
    content: 'translate';
    position: absolute;
    font-size: 11px;
    top: -7px;
    left: -10px;
    padding: 1px 4px;
    color: black;
    background: #f8f8f8;
    box-shadow: 1px 1px 1px 0 rgba(0,0,0,0.4);
    border-radius: 5px;
  }
}
.room-chat-message-loading {
  position: absolute;
  top: 6px;
  left: -6px;
}
.room-chat-message-loading-text {
  font-size: 10px;
}
.room-chat-message {
  font-size: 13px;
  color: #333;
  line-height: 1.6;
  word-break: break-all;
  white-space: break-spaces;
  cursor: default;
  margin-right: 20px;
}
.room-chat-file-info {
  display: flex;
  flex-direction: column;
  gap: 6px 0;
  position: relative;
}
.room-chat-file-name {
  padding-right: 40px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-all;
  cursor: default;
}
.room-chat-file-detail-info {
  display: flex;
  align-items: center;
  cursor: default;
}
.room-chat-file-size {
  font-size: 11px;
  color: #999;
}
.room-chat-file-time {
  font-size: 11px;
  color: #999;
  display: flex;
  align-items: center;
  &::before {
    content: '';
    width: 1px;
    height: 8px;
    margin: 0 8px;
    background-color: #ccc;
    display: block;
  }
}
.room-chat-file-period {
  font-size: 12px;
  color: #999;
  cursor: default;
}
.room-chat-file-progress {
  margin-top: 10px;
  position: relative;
}
.room-chat-file-percent {
  font-size: 12px;
  color: #999;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  position: absolute;
  right: 6px;
  bottom: 10px;
}
.room-chat-file-percent-number {
  margin-right: 6px;
  font-size: 20px;
  font-weight: 700;
  color: #333;
}
.room-chat-file-progress-bar-wrapper {
  height: 2px;
  border-radius: 2px;
  background-color: rgba(0, 0, 0, 0.1);
}
.room-chat-file-progress-bar {
  height: 100%;
  background-color: $mainColor;
}
.room-chat-file-download-button {
  position: absolute;
  right: 6px;
  bottom: 0;
  width: 36px;
  height: 36px;
  border-radius: 100%;
  background: #f4f4f4 url('~@/assets/images/download.svg') no-repeat center / 16px auto;
  &:hover {
    background: $mainColor url('~@/assets/images/download_white.svg') no-repeat center / 16px auto;
  }
}
.room-chat-more {
  position: absolute;
  top: -6px;
  right: 0;
}
.room-chat-more-button {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: url('~@/assets/images/more_vertical.svg') no-repeat center / 3px auto;
  opacity: 0.6;
  &:hover {
    background-color: #f1f1f1;
    opacity: 1;
  }
}

// Component
.grm-menu-component {
  position: absolute;
  top: 4px;
  right: 16px;
}
</style>